import { useState, useRef, useEffect, useMemo } from 'react';
import moment from 'moment';
import { InvoiceCollectionNotesRow } from './types';
import { exportToXlsx, exportToCsv } from './exportUtils';
import { Export, Eye } from '@phosphor-icons/react';
import { DillButton, DillTable } from '../../../../general/Dill';
import type { DillColumn } from '../../../../general/Dill';

// ─── Column helpers ───────────────────────────────────────────────────────────

const textColumn = (
  key: keyof InvoiceCollectionNotesRow,
  label: string,
  options?: { defaultHidden?: boolean; width?: number }
): DillColumn<InvoiceCollectionNotesRow> => ({
  key,
  label,
  width: options?.width ?? 140,
  sortable: true,
  filterable: true,
  toggleable: true,
  defaultHidden: options?.defaultHidden,
  render: (value: string) => <div className="text-xs text-gray-500">{value || '—'}</div>,
});

const numericColumn = (
  key: keyof InvoiceCollectionNotesRow,
  label: string,
  width = 150
): DillColumn<InvoiceCollectionNotesRow> => ({
  key,
  label,
  width,
  sortable: true,
  filterable: true,
  numeric: true,
  toggleable: true,
  headerAlign: 'right',
  render: (value: number) => {
    if (value === 0 || value == null) {
      return <div className="text-xs text-gray-300 text-right">—</div>;
    }
    return (
      <div className="text-xs font-medium text-right tabular-nums text-gray-700">
        {value < 0 ? '-' : ''}$
        {Math.abs(value).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </div>
    );
  },
});

const countColumn = (
  key: keyof InvoiceCollectionNotesRow,
  label: string,
  width = 140
): DillColumn<InvoiceCollectionNotesRow> => ({
  key,
  label,
  width,
  sortable: true,
  filterable: true,
  numeric: true,
  toggleable: true,
  headerAlign: 'right',
  render: (value: number) => (
    <div className="text-xs font-medium text-right tabular-nums text-gray-700">
      {value == null ? '—' : value.toLocaleString('en-US')}
    </div>
  ),
});

// ─── Column definitions ────────────────────────────────────────────────────────

const getColumns = (): DillColumn<InvoiceCollectionNotesRow>[] => [
  textColumn('quickbooksCustomerId', 'Customer Number', { width: 150 }),
  textColumn('businessName', 'Customer Name', { width: 200 }),
  textColumn('invoiceNumber', 'Invoice Number', { width: 150 }),
  numericColumn('invoiceAmount', 'Invoice Amount', 140),
  {
    key: 'daysPastDue',
    label: 'Days Past Due',
    width: 140,
    sortable: true,
    filterable: true,
    numeric: true,
    toggleable: true,
    headerAlign: 'right',
    render: (value: number | null) => {
      if (value == null) return <div className="text-xs text-gray-300 text-right">—</div>;
      return (
        <div
          className={`text-xs font-medium text-right tabular-nums ${
            value >= 30 ? 'text-red-600' : 'text-gray-700'
          }`}>
          {value.toLocaleString('en-US')}
        </div>
      );
    },
  },
  {
    key: 'activitySummary',
    label: 'Activity Summary',
    width: 340,
    sortable: true,
    getSortValue: (row) => (row.lastActivityDate ? new Date(row.lastActivityDate).getTime() : 0),
    filterable: true,
    toggleable: true,
    render: (value: string) => {
      if (!value) return <div className="text-xs text-gray-300">—</div>;
      const lines = value.split('\n').filter(Boolean);
      if (lines.length === 0) return <div className="text-xs text-gray-300">—</div>;
      return (
        <div className="flex flex-col gap-0.5 py-1.5 max-h-[92px] overflow-y-auto pr-1 w-full">
          {lines.map((line, i) => (
            <p key={i} className="text-xs text-gray-500 leading-snug">
              {line}
            </p>
          ))}
        </div>
      );
    },
  },
  {
    key: 'lastActivityDate',
    label: 'Last Activity Date',
    width: 160,
    sortable: true,
    filterable: true,
    toggleable: true,
    render: (value: string) => {
      if (!value) return <div className="text-xs text-gray-500">—</div>;
      return <div className="text-xs text-gray-500">{moment(value).format('MM/DD/YYYY')}</div>;
    },
  },
  countColumn('activityCount', 'Activity Count', 140),
  {
    key: 'collectionStatus',
    label: 'In Sequence/Trigger',
    width: 150,
    sortable: true,
    filterable: true,
    toggleable: true,
    render: (value: InvoiceCollectionNotesRow['collectionStatus']) => {
      if (value === 'Sequence') {
        return (
          <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700">
            Sequence
          </span>
        );
      }
      if (value === 'Trigger') {
        return (
          <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">
            Trigger
          </span>
        );
      }
      return (
        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">
          No
        </span>
      );
    },
  },
  textColumn('collectionName', 'Sequence/Trigger Name', { width: 200 }),
  {
    key: 'collectionStep',
    label: 'Sequence Step',
    width: 220,
    sortable: true,
    filterable: true,
    toggleable: true,
    render: (value: string) => (
      <div className="text-xs text-gray-500 line-clamp-2" title={value || undefined}>
        {value || '—'}
      </div>
    ),
  },
];

// ─── Component ─────────────────────────────────────────────────────────────────

interface InvoiceCollectionNotesTableProps {
  data: InvoiceCollectionNotesRow[];
  isLoading?: boolean;
}

export const InvoiceCollectionNotesTable = ({
  data,
  isLoading,
}: InvoiceCollectionNotesTableProps) => {
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isColumnsOpen, setIsColumnsOpen] = useState(false);
  // Rows after the table's search/filters/sorting are applied — what we export.
  const [filteredData, setFilteredData] = useState<InvoiceCollectionNotesRow[]>(data);
  const exportRef = useRef<HTMLDivElement>(null);
  const columnsRef = useRef<HTMLDivElement>(null);

  const allColumns = useMemo(() => getColumns(), []);

  const [hiddenCols, setHiddenCols] = useState<Set<string>>(
    () => new Set(allColumns.filter((c) => c.defaultHidden).map((c) => c.key))
  );

  const visibleColumns = useMemo(
    () => allColumns.filter((c) => !hiddenCols.has(c.key)),
    [allColumns, hiddenCols]
  );

  const toggleColumn = (key: string) => {
    setHiddenCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        // Don't allow fewer than 3 visible columns
        const visibleCount = allColumns.filter((c) => !next.has(c.key)).length;
        if (visibleCount <= 3) return prev;
        next.add(key);
      }
      return next;
    });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportRef.current && !exportRef.current.contains(event.target as Node)) {
        setIsExportOpen(false);
      }
      if (columnsRef.current && !columnsRef.current.contains(event.target as Node)) {
        setIsColumnsOpen(false);
      }
    };

    if (isExportOpen || isColumnsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExportOpen, isColumnsOpen]);

  const exportColumns = useMemo(
    () =>
      visibleColumns.map((c) => ({
        key: c.key,
        label: typeof c.label === 'string' ? c.label : c.key,
        width: c.width,
      })),
    [visibleColumns]
  );

  const handleExportXlsx = () => {
    exportToXlsx(filteredData, exportColumns);
    setIsExportOpen(false);
  };

  const handleExportCsv = () => {
    exportToCsv(filteredData, exportColumns);
    setIsExportOpen(false);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex flex-col">
      <div className="mb-4">
        <p className="text-sm text-gray-500">
          Every open invoice is listed below, with any collection activity mapped alongside it.
          Filter, sort, and adjust columns, or export to Excel or CSV.
        </p>
      </div>

      <DillTable<InvoiceCollectionNotesRow>
        columns={visibleColumns}
        data={data}
        onProcessedDataChange={setFilteredData}
        rowKey={(r) => r.id}
        verticalDividers
        search
        searchPlaceholder="Search invoices…"
        pagination
        pageSize={25}
        rowHeight={110}
        scrollX
        showActiveFilters
        striped={false}
        maxHeight="calc(100vh - 280px)"
        showFooter={false}
        loading={isLoading}
        loadingText="Loading invoice collection notes..."
        emptyMessage="No invoices match your filters"
        showCount={false}
        virtualize
        toolbarRight={
          <div className="flex gap-2">
            {/* Column visibility dropdown */}
            <div className="relative" ref={columnsRef}>
              <DillButton
                label="Columns"
                onClick={() => setIsColumnsOpen(!isColumnsOpen)}
                icon={<Eye size={14} />}
                variant="ghost"
              />
              {isColumnsOpen && (
                <div className="absolute right-0 z-50 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-2 w-56 max-h-[400px] overflow-y-auto">
                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100 flex items-center justify-between">
                    <span>Show/Hide Columns</span>
                    <button
                      onClick={() => setHiddenCols(new Set())}
                      className="text-[10px] font-semibold text-blue-600 hover:text-blue-700 normal-case tracking-normal">
                      Select All
                    </button>
                  </div>
                  {allColumns.map((col) => {
                    const isVisible = !hiddenCols.has(col.key);
                    const canHide = isVisible && visibleColumns.length <= 3;
                    return (
                      <label
                        key={col.key}
                        className={`flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 ${
                          canHide ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                        }`}>
                        <input
                          type="checkbox"
                          checked={isVisible}
                          onChange={() => toggleColumn(col.key)}
                          disabled={canHide}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-100/80 disabled:opacity-50"
                        />
                        <span className="flex-1">{col.label}</span>
                        {canHide && (
                          <span className="text-[10px] text-gray-400">Min 3 required</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <div
              className="relative"
              ref={exportRef}
              title={isLoading ? 'Still loading invoice collection notes…' : undefined}>
              <DillButton
                label="Export"
                onClick={() => setIsExportOpen(!isExportOpen)}
                icon={<Export size={14} />}
                variant="primary"
                isDisabled={isLoading}
              />
              {isExportOpen && (
                <div className="absolute right-0 z-50 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 w-40">
                  <button
                    onClick={handleExportXlsx}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-100/80 focus:text-blue-600">
                    Export as XLSX
                  </button>
                  <button
                    onClick={handleExportCsv}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-100/80 focus:text-blue-600">
                    Export as CSV
                  </button>
                </div>
              )}
            </div>
          </div>
        }
      />
    </div>
  );
};
